import {
  Box,
  keyframes,
  type PlacementWithLogical,
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import type { SingleValue } from 'chakra-react-select';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useCookie } from 'react-use';

import { useUserTrialDetails } from '@revelio/auth';
import { FeatureFlag } from '@revelio/core';

import { usePersistentlyPlacedPopover } from '../../../../shared/hooks/usePersistentlyPlacedPopover';
import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import { UserPreferenceControlledFilterIds } from '../../../../shared/user-preferences/types';
import {
  usePageConfig,
  useTabContentConfig,
} from '../../components/PageConfigProvider/PageConfigProvider';
import { shouldShowAiSearchForPage } from '../ai-search/shouldShowAiSearchForPage';
import {
  ALL_FILTER_DEFINITIONS,
  getFilterDefinition,
} from '../engine/filterRegistry';
import { useFilters } from '../hooks/useFilters';
import { useMultiFilterEditor } from '../hooks/useMultiFilterEditor';
import {
  type Filter,
  FilterId,
  type FilterSelectOptions,
  FilterType,
} from '../types/filters';
import { isTreeFilter } from '../utils/common';
import { canFilterBeSegmented } from '../utils/filterSegmentation';
import styles from './FilterPopover.module.css';
import { FilterSelectDropdown } from './FilterSelectDropdown';
import { FilterSelectList } from './FilterSelectList';
import { DEFAULT_AVAILABLE_FILTER_IDS } from './FilterSelectModal';
import { onFilterMenuOpenRequest } from './filter-menu-open-signal';
import { FilterFormRender } from './filterForms/FilterFormRender';
import {
  getGroupFilterOptions,
  getSubFilterLabelPostfix,
  SubFilter,
} from './SubFilter';

interface FilterPopoverProps {
  trigger: ReactNode;
  filter?: Filter;
  placement?: PlacementWithLogical;
}

const pulseKeyframes = keyframes`
0% {
  transform: scale(1);
  opacity: 0.5;
}
100% {
  transform: scale(4);
  opacity: 0;
}
`;

const getRegularFilterOptions = (
  allOptions: FilterSelectOptions[],
  primaryFilterId?: FilterId
): FilterSelectOptions[] => {
  const result: FilterSelectOptions[] = [];

  allOptions.forEach((option) => {
    if (option.id === 'AI_SEARCH') {
      result.push(option);
      return;
    }

    const filterId: FilterId = option.id;
    const filterDef = ALL_FILTER_DEFINITIONS[filterId];

    // If it's a GROUP filter, expand it to show its children
    if (filterDef?.type === FilterType.GROUP) {
      const childOptions = getGroupFilterOptions(filterId, primaryFilterId);
      result.push(...childOptions);
    } else {
      result.push(option);
    }
  });

  return result;
};

const shouldSelectFilter = (filter: FilterSelectOptions): boolean => {
  if (filter.id === 'AI_SEARCH') {
    return true;
  }

  const filterId: FilterId = filter.id;
  const filterDef = ALL_FILTER_DEFINITIONS[filterId];
  return !filterDef?.children || filterDef.children.length === 0;
};

export const FilterPopover = ({
  trigger,
  filter,
  placement = 'bottom-start',
}: FilterPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { contentRef } = usePersistentlyPlacedPopover(isOpen);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { filters } = useFilters();

  const {
    config,
    revelioPageId: revelioPageIdFromTab,
    primaryFilter,
  } = useTabContentConfig();
  const { config: pageConfig } = usePageConfig();
  const revelioPageId = revelioPageIdFromTab ?? pageConfig?.revelioPageId;
  const { secondaryFilters } = config;
  const isCustomPage = !revelioPageId;
  const isAiSearchWiderDashboardEnabled = useUnleashFlag(
    FeatureFlag.AiSearchWiderDashboard
  );
  const isValuationEnabled = useUnleashFlag(
    FeatureFlag.CompanyDiscoveryValuationFilter
  );

  const {
    selectedFilter,
    selectedFilterId,
    selectedMultiMeta,
    selectFilter,
    clearSelection,
  } = useMultiFilterEditor({
    filters,
    initialFilter: filter,
    isOpen,
  });

  const showSubFilter = selectedFilter && canFilterBeSegmented(selectedFilter);

  const editFiltersModalDisclosure = useDisclosure();

  // Get available filter IDs based on page configuration
  const availableFilterIds: FilterId[] = (() => {
    // If page has explicit secondaryFilters configured, use those
    if (secondaryFilters.length > 0) {
      return secondaryFilters.map((f) => f.id);
    }

    // For custom pages with no secondaryFilters, use the whitelist
    if (isCustomPage) {
      return Array.from(filters.keys()).filter((f) =>
        DEFAULT_AVAILABLE_FILTER_IDS.includes(f)
      );
    }

    // For core Revelio pages with no secondaryFilters, show all filters (existing behavior)
    return Array.from(filters.keys()).filter((f) => {
      return ![
        FilterId.MULTI_TITLE,
        FilterId.MULTI_KEYWORDS,
        FilterId.MULTI_CUSTOM_SKILL,
      ].includes(f);
    });
  })();

  // Match wrapper/provider gating so AI Search menu option only appears where supported.
  const shouldShowAiSearch = pageConfig
    ? shouldShowAiSearchForPage({
        pageType: pageConfig.type,
        revelioPageId,
        primaryFilterId: primaryFilter?.id,
        isAiSearchWiderDashboardEnabled,
      })
    : false;

  const aiSearchFilterOption = shouldShowAiSearch
    ? [
        {
          id: 'AI_SEARCH' as const,
          name: 'AI Search',
        },
      ]
    : [];

  const filterOptions: FilterSelectOptions[] = [
    ...aiSearchFilterOption,
    ...availableFilterIds
      .filter(
        (filterId) => !UserPreferenceControlledFilterIds.includes(filterId)
      )
      .filter((filterId) => {
        // Always exclude the legacy SKILL filter since it gets migrated to CUSTOM_SKILL
        if (filterId === FilterId.SKILL) {
          return false;
        }
        return true;
      })
      .map<FilterSelectOptions>((filterId) => {
        const filterDefinition = getFilterDefinition(filterId);
        return {
          id: filterId,
          name: filterDefinition?.name ?? String(filterId),
          labelPostfix: getSubFilterLabelPostfix(filterId, primaryFilter?.id),
        };
      })
      .sort((a, b) => {
        const an = a?.name ?? '';
        const bn = b?.name ?? '';
        return an.localeCompare(bn);
      }),
  ];

  // Filter options for the main menu (excluding only child filters of GROUP filters)
  const mainMenuFilterOptions = filterOptions.filter((option) => {
    if (option.id === 'AI_SEARCH') {
      return true;
    }

    const filterId: FilterId = option.id;
    const isChildFilter = Object.values(ALL_FILTER_DEFINITIONS).some(
      (def) => def.type === FilterType.GROUP && def.children?.includes(filterId)
    );

    return !isChildFilter;
  });

  const handleOpenFilterMenu = () => {
    if (!filter) {
      clearSelection();
    }
    setIsOpen(true);
  };

  // Only the main filter menu (no bound filter) answers open requests from
  // elsewhere on the page, e.g. the screener empty-state CTA.
  useEffect(() => {
    if (filter) {
      return;
    }
    return onFilterMenuOpenRequest(() => {
      clearSelection();
      setIsOpen(true);
    });
  }, [filter, clearSelection]);

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      clearSelection();
    }, 100);
  };

  const handleFilterChange = (
    selected: SingleValue<{
      label: string;
      value: FilterSelectOptions['id'];
    }>
  ) => {
    if (selected) {
      selectFilter(selected.value);
    } else {
      clearSelection();
    }
  };

  const [filterAttention, updateFilterAttention] =
    useCookie('filter-attention');

  const { isSelfSignupUser } = useUserTrialDetails<boolean>({
    initialRenderValue: false,
  });

  useEffect(() => {
    if (!filterAttention && isOpen && !filter) {
      updateFilterAttention('false');
    }
  }, [filterAttention, updateFilterAttention, isOpen, filter]);

  const shouldShowAttentionIndicator =
    isSelfSignupUser && !filterAttention && !filter;

  const selectedFilterDef =
    selectedFilterId && selectedFilterId !== 'AI_SEARCH'
      ? ALL_FILTER_DEFINITIONS[selectedFilterId]
      : null;

  const dropdownOptions = (() => {
    let options: FilterSelectOptions[];
    if (
      !selectedFilterDef ||
      !selectedFilterId ||
      selectedFilterId === 'AI_SEARCH'
    ) {
      options = filterOptions;
    } else if (selectedFilterDef.type === FilterType.GROUP) {
      options = getGroupFilterOptions(selectedFilterId, primaryFilter?.id);
    } else {
      options = getRegularFilterOptions(filterOptions, primaryFilter?.id);
    }

    if (!isValuationEnabled) {
      return options.filter((o) => o.id !== FilterId.VALUATION);
    }
    return options;
  })();

  return (
    <Popover
      placement={placement}
      onClose={handleClose}
      isOpen={isOpen && !editFiltersModalDisclosure.isOpen}
      onOpen={handleOpenFilterMenu}
      initialFocusRef={searchInputRef}
    >
      <PopoverTrigger>
        <Box position="relative" display="inline-block">
          {trigger}
          {shouldShowAttentionIndicator && (
            <Box
              className={styles.indicatorRing}
              width="11px"
              height="11px"
              bgGradient="linear(to-r, #FF4567, #FF4567)"
              position="absolute"
              top="-4px"
              right="-4px"
              borderRadius="full"
              pointerEvents="none"
              zIndex="10"
              _before={{
                content: `""`,
                position: 'absolute',
                width: '100%',
                height: '100%',
                backgroundColor: '#FF4567',
                borderRadius: 'full',
                zIndex: '-1',
                opacity: '0.5',
                left: '0px',
                animation: `${pulseKeyframes} 3s ease-out infinite`,
              }}
              _after={{
                content: `""`,
                position: 'absolute',
                width: '100%',
                height: '100%',
                backgroundColor: '#FF4567',
                borderRadius: 'full',
                zIndex: '-1',
                opacity: '0.5',
                left: '0px',
                animation: `${pulseKeyframes} 3s 1s ease-out infinite`,
              }}
            />
          )}
        </Box>
      </PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        className={styles.popover}
        data-testid={`filter-popover-content-${isOpen ? 'open' : 'closed'}`}
      >
        {(() => {
          if (!isOpen) {
            return null;
          }

          if (!selectedFilter && selectedFilterId !== 'AI_SEARCH') {
            return (
              <FilterSelectList
                filterOptions={mainMenuFilterOptions}
                setSelectedFilter={(filter) => {
                  if (shouldSelectFilter(filter)) {
                    selectFilter(filter.id);
                  }
                }}
                closeMenu={handleClose}
                editFiltersModalDisclosure={editFiltersModalDisclosure}
                searchInputRef={searchInputRef}
              />
            );
          }
          if (showSubFilter) {
            return (
              <SubFilter
                filterSelectDropdown={
                  !filter ? (
                    <FilterSelectDropdown
                      filterOptions={dropdownOptions}
                      selectedFilter={selectedFilterId}
                      onChangeSelectedFilter={handleFilterChange}
                    />
                  ) : (
                    <Text fontSize="sm" className={styles.mutedName}>
                      {selectedFilter?.name}
                    </Text>
                  )
                }
                filter={selectedFilter}
                close={handleClose}
                selectedMultiMeta={selectedMultiMeta}
                selectedFilterId={selectedFilterId}
              />
            );
          }
          return (
            <div data-testid="filter-menu-popover">
              {!filter && (
                <PopoverHeader
                  marginBottom={2}
                  padding={0}
                  fontWeight="semibold"
                  borderBottomWidth={0}
                >
                  <FilterSelectDropdown
                    filterOptions={dropdownOptions}
                    selectedFilter={selectedFilterId}
                    onChangeSelectedFilter={handleFilterChange}
                  />
                </PopoverHeader>
              )}
              <FilterFormRender
                key={selectedFilter?.id ?? 'none'}
                filter={selectedFilter}
                close={handleClose}
                selectedMultiMeta={selectedMultiMeta}
                selectedFilterId={selectedFilterId}
                showSelectedValues={false}
                currentTreeSelections={
                  selectedFilter && isTreeFilter(selectedFilter)
                    ? selectedFilter.selections
                    : undefined
                }
              />
            </div>
          );
        })()}
      </PopoverContent>
    </Popover>
  );
};
